import {
  RelationshipAccessLevel,
  type WorkflowWithActionGroups,
  type Property,
  type HandoffWorkflowConfiguration,
  type WeeklySchedule,
  parseOfficeHours,
  getPropertyIdsFromFilterConditions,
  getPropertyGroupIdsFromFilterConditions,
} from '@super/shared-types'
import type { HandoffRule } from '@/contexts/handoff-workflows.context'

/**
 * Transform a workflow from the API format to the UI HandoffRule format
 */
export function transformWorkflowToHandoffRule(
  workflow: WorkflowWithActionGroups,
  properties: Property[],
): HandoffRule {
  // Parse configuration with proper typing
  const config = workflow.configuration as HandoffWorkflowConfiguration
  const originalData = config?.originalFormData || {}

  // Property/group scope hydrates from filterConditions — the single source
  // of truth (a second stored copy could desync and resurrect deleted ids).
  const propertyIds = getPropertyIdsFromFilterConditions(
    workflow.filterConditions,
  )
  const propertyGroupIds = getPropertyGroupIdsFromFilterConditions(
    workflow.filterConditions,
  )
  const propertyObjects = propertyIds
    .map(id => properties.find(p => p.id === id))
    .filter(Boolean) as Property[]

  // Extract group types
  const groupTypes = (originalData.groupTypes ||
    []) as RelationshipAccessLevel[]
  const customOfficeHoursSchedule = originalData.customOfficeHoursSchedule as
    | WeeklySchedule
    | undefined

  return {
    id: workflow.id,
    title: workflow.name,
    selectionCriteria: workflow.selectionCriteria || '',
    propertyIds: propertyIds,
    propertyGroupIds: propertyGroupIds,
    propertyNames: propertyObjects.map(p => p.name || 'Unknown Property'),
    groupTypes: groupTypes,
    officeHours: parseOfficeHours(originalData.officeHours),
    ...(customOfficeHoursSchedule ? { customOfficeHoursSchedule } : undefined),
    handoffInstructions: config?.handoffInstructions || '',
    actionGroups: workflow.actionGroups.map(group => ({
      id: group.id,
      name: group.name,
      selectionCriteria: group.selectionCriteria,
      selectionType: group.selectionType,
      actions:
        group.actions?.map(action => ({
          actionDefinition: { actionType: action.actionType },
          parameters: action.parameters,
          integrationId: action.integrationId,
          actionSubtype: action.actionSubtype,
        })) || [],
    })),
    createdAt: new Date(workflow.createdAt),
    updatedAt: new Date(workflow.updatedAt),
  }
}
